/**
 * Plan rotation, written once: degrees and radians, turning a direction, and
 * turning a point about a pivot. Free elements, whole-room placement, the
 * quarter-turn edit and the raw-scan alignment all turn through these.
 */
import type { Point } from '../schemas';

/** Degrees to radians. */
export const degreesToRadians = (degrees: number): number => (degrees * Math.PI) / 180;

/** Degrees wrapped into [0, 360). */
export const normalizeDegrees = (degrees: number): number => ((degrees % 360) + 360) % 360;

/** A direction turned by `radians` (no translation). */
export function rotateVector(vector: Point, radians: number): Point {
  const cosine = Math.cos(radians), sine = Math.sin(radians);
  return { x: vector.x * cosine - vector.y * sine, y: vector.x * sine + vector.y * cosine };
}

/**
 * `point` turned about `pivot` by the angle with this cosine and sine. The
 * turned offset is laid off from `landing`: the pivot itself, unless the
 * turn also carries the pivot somewhere else.
 */
export function rotateAbout(point: Point, pivot: Point, cosine: number, sine: number, landing: Point = pivot): Point {
  const dx = point.x - pivot.x, dy = point.y - pivot.y;
  return { x: landing.x + dx * cosine - dy * sine, y: landing.y + dx * sine + dy * cosine };
}

/** Radians wrapped into [-π, π), leaving an angle already in range exactly as it is: the bearings the scan intake writes. */
export const wrapRadians = (radians: number): number => radians - 2 * Math.PI * Math.floor((radians + Math.PI) / (2 * Math.PI));

/**
 * Radians wrapped into (-π, π] by remainders: the bearings the quarter-turn
 * edit writes. It can move an angle already in range in its last bit, so it
 * stays apart from `wrapRadians` to keep saved bearings exactly as they are.
 */
export function wrapRadiansUpToPi(radians: number): number {
  const full = 2 * Math.PI;
  const wrapped = ((radians % full) + full) % full;
  return wrapped > Math.PI ? wrapped - full : wrapped;
}
